import { DecimalPipe, NgClass, TitleCasePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, signal } from '@angular/core';
import { AbstractDynamicComponent } from '@components/abstracts';
import { PopinAvisGoogleDetailsComponent } from '@components/avis-google-details/popin-avis-google-details';
import { EncartInfosMagasin, WeeklySchedule } from '@components/encart-infos-magasin/models';
import { TranslatePipe } from '@ngx-translate/core';
import { ButtonMiddleOfficeComponent } from '@shared/components/button-middle-office';
import { LoaderComponent } from '@shared/components/loader';
import { SvgComponent } from '@shared/components/svg';
import { TitleComponent } from '@shared/components/title';
import { Title } from '@shared/components/title/models';
import { ClickOutsideDirective } from '@shared/directives';
import { StoreSchedule, Weekday } from '@shared/models';
import { DialogService } from '@shared/services/dialog';
import { ElasticSearchService } from '@shared/services/elastic-search';
import { CurrentLanguageService, LanguageService } from '@shared/services/language';
import { LogsService } from '@shared/services/logs';
import { MiddleOfficeService } from '@shared/services/middle-office';
import { SeoService } from '@shared/services/seo';
import { JsonLD } from '@shared/services/seo/models';
import { formatPhone, getLocaleDateFns, getStoreStatus } from '@shared/utils';
import { format } from 'date-fns';
import { take, tap } from 'rxjs';

@Component({
  selector: 'app-encart-infos-magasin',
  imports: [
    TitleComponent,
    SvgComponent,
    NgClass,
    TranslatePipe,
    LoaderComponent,
    ButtonMiddleOfficeComponent,
    DecimalPipe,
    ClickOutsideDirective,
  ],
  providers: [TitleCasePipe],
  templateUrl: './encart-infos-magasin.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EncartInfosMagasinComponent extends AbstractDynamicComponent<EncartInfosMagasin> {
  readonly isScheduleDisplayed = signal(false);
  readonly weeklySchedule = signal<WeeklySchedule>(null!);
  readonly isStoreScheduleLoaded = signal(false);
  protected readonly googleMapLink = computed(() => {
    const query = encodeURIComponent(
      `SoCoo'c ${this.data.store.streetName} ${this.data.store.zipcode} ${this.data.store.city}`
    );
    return `https://www.google.com/maps/search/?api=1&query=${query}`;
  });
  protected readonly phone = computed(() => formatPhone(this.data.store.phone ?? ''));
  protected readonly isMiddleOfficeAvailable = inject(MiddleOfficeService).isMiddleOfficeAvailable;
  protected readonly titleStoreName = computed<Title>(() => {
    const raw = this.data.store.name ?? '';
    const city = raw.replace(/^SoCoo'c\s*/i, '').trim();
    return { text: `[[SoCoo'c]]\\\\ ${city}`, as: 'h1' };
  });
  private readonly languageService = inject(LanguageService);
  private readonly language = inject(CurrentLanguageService).language;
  private readonly dialogService = inject(DialogService);
  private readonly elasticSearchService = inject(ElasticSearchService);
  private readonly titleCasePipe = inject(TitleCasePipe);
  private readonly seoService = inject(SeoService);
  private readonly logs = inject(LogsService);

  constructor() {
    super();
    effect(() => {
      const storeUuid = this.data.store.uuid;
      if (storeUuid) {
        this.injectStoreJsonLd();
        this.elasticSearchService
          .getStoreSchedule(storeUuid)
          .pipe(
            take(1),
            tap((schedule) => {
              this.weeklySchedule.set(this.formatWeeklySchedule(schedule));
              this.isStoreScheduleLoaded.set(true);
            })
          )
          .subscribe({
            error: (err) => this.logs.error('EncartInfosMagasinComponent', 'getStoreSchedule failed', err),
          });
      }
    });
  }

  showAllReviews() {
    this.dialogService.openDialog(PopinAvisGoogleDetailsComponent, 'BIG', {
      data: {
        title: { text: `${this.data.store.name}`, as: 'h2' },
        reviewStats: this.data.reviewStats,
        storeId: this.data.store.uuid,
      },
    });
  }

  onDisplaySchedule() {
    if (this.weeklySchedule().operationalStatus) return;
    this.isScheduleDisplayed.update((displayed) => !displayed);
  }

  private injectStoreJsonLd() {
    const review = this.data.reviewStats;
    const store = this.data.store;
    const schema: JsonLD = {
      '@context': 'https://schema.org',
      '@type': 'Store',
      brand: `SoCoo'c`,
      name: store.name,
      currenciesAccepted: 'EUR',
      paymentAccepted: 'Cash, Credit Card, Local Exchange Tradings System',
      address: {
        '@type': 'PostalAddress',
        streetAddress: store.streetName,
        addressLocality: store.city,
        postalCode: store.zipcode,
        addressCountry: this.language.region,
      },
      telephone: store.phone,
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: review.averageRating || 0,
        reviewCount: review.totalReviews || 0,
      },
    };
    this.seoService.addJsonLdScript('ecart-infos-magasin-schema', schema);
  }

  private formatWeeklySchedule(storeSchedule: StoreSchedule): WeeklySchedule {
    const weekdays: Weekday[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    const locale = getLocaleDateFns(this.language.locale);

    const EXCEPTIONAL_CLOSURE = this.languageService.instant('COMPONENT.ENCART-INFOS-MAGASIN.EXCEPTIONAL_CLOSURE');
    const CLOSED = this.languageService.instant('COMPONENT.ENCART-INFOS-MAGASIN.CLOSED');

    const baseDate = new Date(2021, 0, 4);

    const storeStatus = getStoreStatus(storeSchedule.weeklyOpeningHours);

    const formattedScheduleDay = weekdays.map((day, index) => {
      const dayData = storeSchedule.weeklyOpeningHours[day];

      const dayDate = new Date(baseDate);
      dayDate.setDate(baseDate.getDate() + index);

      const translatedDay = this.titleCasePipe.transform(format(dayDate, 'EEEE', { locale }));

      let openingHours: string;

      if (!dayData.isOpen) openingHours = dayData.isExceptionalClosure ? EXCEPTIONAL_CLOSURE : CLOSED;
      else openingHours = dayData.timeSlots.map((slot) => `${slot.start} : ${slot.end}`).join(' - ');

      return { translatedDay, openingHours };
    });

    return {
      storeStatus,
      formattedScheduleDay,
      exceptionalOpenings: storeSchedule.exceptionalOpenings,
      exceptionalClosures: storeSchedule.exceptionalClosures,
      operationalStatus: storeSchedule.operationalStatus,
    };
  }
}
